import React from 'react'
import { observer, inject } from 'mobx-react'
import { StateType } from '../state/state'
import { kebabCase } from 'lodash-es'
import { ScaleSymLog, ScaleLinear } from 'd3-scale'
import { DOT_RADIUS, ANIMATION_DURATION_SEC, ACTIVE_DOT_RADIUS } from '../constants'
import { AnimatedCircle } from '../../../components/AnimatedCircle'

interface Props {
  className?: string
  state?: StateType
  xScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
  yScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
}

export const Dots: React.FC<Props> = inject('state')(
  observer(({ className = '', state, xScale, yScale }) => {
    const {
      circles,
      isCountryOvered,
      isCountryActive,
      setOveredCountry,
      resetOveredCountry,
      toggleActiveCountry,
      isIntoDomains,
      activeOrOveredCountries,
    } = state.ScatterplotState

    const isThereADotSelectedOrOvered = activeOrOveredCountries.length > 0

    return (
      <g className={`-dots ${className}`}>
        {circles.map((datum) => {
          const { x, y, country } = datum
          const isOvered = isCountryOvered(country)
          const isActive = isCountryActive(country)
          const cx = xScale(x)
          const cy = yScale(y)
          const xInvert = xScale.invert(cx)
          const yInvert = yScale.invert(cy)

          // filter points: remove those points whose are outside the current domain

          // `x` is the computed value in the state. it could be NA
          // `cx` is the scaled value. since the scale remaps the missing data with `xUnknownValue`, then `cx` is never NaN
          // I have to show only the points that are actually in the visible domain (which may be smaller due to the sliders).
          // I can't do `isIntoDomains(x, y)` because `x` and `y` can be NA so they would always be out of the domain and wouldn't be drawn.
          // So I have to check the value passed in the scale that are `xInverted` and `yInverted`.
          const isPointIntoDomains = isIntoDomains(xInvert, yInvert)

          const opacity = isThereADotSelectedOrOvered ? (isActive || isOvered ? 0.8 : 0.2) : 0.4

          return (
            <g className={`-dot-${kebabCase(country)}`} key={kebabCase(country)}>
              {/* do not draw the datum if the center is outside the domain */}
              {!isPointIntoDomains ? null : (
                <>
                  <AnimatedCircle
                    className="pointer"
                    cx={cx}
                    cy={cy}
                    r={DOT_RADIUS}
                    fill={'(var--black)'}
                    fillOpacity={opacity}
                    onMouseOver={() => setOveredCountry(country)}
                    onMouseOut={() => resetOveredCountry()}
                    onClick={() => toggleActiveCountry(country)}
                    animationDurationSec={ANIMATION_DURATION_SEC}
                  />
                  <AnimatedCircle
                    className="-active-bigger-circle pointer-events-none"
                    cx={cx}
                    cy={cy}
                    r={isActive ? ACTIVE_DOT_RADIUS : 0}
                    fill="none"
                    stroke="var(--blue400)"
                  />
                </>
              )}
            </g>
          )
        })}
      </g>
    )
  })
)
